/**
 * Selectable stage (Local Cameras) or media-laptop tracks for perform category modals.
 * Stage cameras: `selectTrack` cue-command under auto-cue, else a staged-selection update.
 * Media laptops are demo screens: their screen share is main content, like in the media list.
 */

import { useMemo } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { LiveContextStateDto } from '@/state/queries/player/use-get-live-context-state';
import type { PlayerDto } from '@/types/dtos/player/response/player.dto';
import { useGetPlayersQuery } from '@/state/queries/players/use-get-players';
import { resolveLiveBindingFlowId } from '@/helpers/object.id.helper';
import { useCueCommandMutation } from '@/state/mutations/player/use-cue-command';
import { useUpdateStagedSelectionMutation } from '@/state/mutations/player/use-update-staged-selection';
import { useFeatureAccess } from '@/hooks/use-feature-access';
import { useLiveContextCueState } from '@/hooks/use-live-context-cue-state';
import { useTranslations } from '@/i18n/use-translations';
import { LiveKitPreviewConnector } from '@/components/preview/livekit-preview-connector';
import {
  buildPlayedItemIdsSet,
  cuePhaseToRowState,
  getPerformItemRowStyles,
  getPerformSelectableRowHoverSx,
  isPerformItemOnAir,
  performItemRowBaseSx,
  resolveCueItemState,
  toggleMainContentInSelection,
} from '@/helpers/perform-staging.helper';

export type PerformTrackCategory = 'cam' | 'remote';

export interface PerformCategoryTrackPickerProps {
  category: PerformTrackCategory;
  flow: FlowDto;
  entityRef?: string;
  eventSlotId?: string;
  livekitRoomId?: string;
  liveContextState?: LiveContextStateDto;
  onTrackSelected?: () => void;
}

const GRID_LAYOUT_SX = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
  gap: 2,
  width: '100%',
} as const;

function resolveItemId(category: PerformTrackCategory, player: PlayerDto): string {
  return category === 'cam' ? `roomCam:${player.id}-cam1` : `screenshare:${player.id}`;
}

export function PerformCategoryTrackPicker({
  category,
  flow,
  entityRef,
  eventSlotId,
  livekitRoomId,
  liveContextState,
  onTrackSelected,
}: PerformCategoryTrackPickerProps) {
  const t = useTranslations();
  const { hasAccess: hasAutoCue } = useFeatureAccess('perform_auto_cue');
  const cueCommand = useCueCommandMutation();
  const updateStagedSelection = useUpdateStagedSelectionMutation();
  const bindingFlowId = resolveLiveBindingFlowId(flow, eventSlotId);
  const liveReady = flow?.liveContext?.isRunning ?? false;

  const { data: players = [] } = useGetPlayersQuery(
    bindingFlowId
      ? {
          entityId: bindingFlowId,
          entityType: 'Flow',
          ...(eventSlotId ? { eventSlotId } : {}),
        }
      : undefined,
    { enabled: !!bindingFlowId && liveReady },
  );

  const trackPlayers = useMemo(() => {
    if (!liveReady || !Array.isArray(players)) return [];
    if (category === 'cam') {
      return players.filter((p): p is PlayerDto => p.type === 'stagePlayer');
    }
    return players.filter((p): p is PlayerDto => p.type === 'screenPublisher' && p.isConnected);
  }, [category, liveReady, players]);

  const selectedItemIds = liveContextState?.stagedSelection?.selectedItemIds ?? [];
  const rawShowMode = liveContextState?.stagedSelection?.showMode ?? 'mediaOnly';
  const showMode: 'mediaOnly' | 'both' | 'speakerOnly' = rawShowMode === 'mixedView' ? 'both' : rawShowMode;
  const playedItemIds = useMemo(() => buildPlayedItemIdsSet(liveContextState), [liveContextState]);
  const cueState = useLiveContextCueState(entityRef, undefined, liveContextState?.cueState);
  const cueItems = cueState?.items;
  const usesAutoCue = hasAutoCue && !!cueState;

  const handleSelect = (itemId: string) => {
    if (!entityRef) return;

    const onSuccess = () => onTrackSelected?.();

    if (usesAutoCue && category === 'cam') {
      cueCommand.mutate({ action: 'selectTrack', entityRef, itemId }, { onSuccess });
      return;
    }

    const isSelected = selectedItemIds.includes(itemId);
    const next =
      category === 'remote'
        ? toggleMainContentInSelection(selectedItemIds, itemId)
        : isSelected
          ? selectedItemIds.filter((id) => id !== itemId)
          : [...selectedItemIds, itemId];
    updateStagedSelection.mutate(
      {
        entityRef,
        stagedSelection: {
          selectedItemIds: next,
          showMode,
          ...(next.length === 0 ? { clearSelection: true } : {}),
        },
      },
      { onSuccess },
    );
  };

  if (!livekitRoomId) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
        {t('flows.detail.perform.categoryModal.noLiveContext', {
          defaultValue: 'Live context not available. Start the flow to connect.',
        })}
      </Typography>
    );
  }

  if (trackPlayers.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'action.hover' }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          {category === 'cam'
            ? t('flows.detail.perform.noStages', { defaultValue: 'No stage cameras' })
            : t('flows.detail.perform.noMediaLaptops', {
                defaultValue: 'No media laptops connected. Media laptop players appear here when connected.',
              })}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('flows.detail.perform.categoryModal.noTracksHint', {
            defaultValue: 'Connect players to the flow to select tracks here.',
          })}
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={GRID_LAYOUT_SX}>
      {trackPlayers.map((player) => {
        const itemId = resolveItemId(category, player);
        const legacySelected = selectedItemIds.includes(itemId);
        const legacyPlayed = playedItemIds.has(itemId);
        const cueTracked = usesAutoCue && category === 'cam';
        const { staged, played } = cueTracked
          ? cuePhaseToRowState(resolveCueItemState(cueItems, itemId)?.phase)
          : { staged: legacySelected, played: legacyPlayed };
        const cuePhase = cueTracked ? resolveCueItemState(cueItems, itemId)?.phase : undefined;
        const isOnAir = isPerformItemOnAir(cueTracked, cuePhase, staged, played);
        const canSelect = !!entityRef && !cueCommand.isPending && !updateStagedSelection.isPending;

        return (
          <Paper
            key={player.id}
            elevation={staged || played ? 4 : 1}
            onClick={() => canSelect && handleSelect(itemId)}
            sx={
              {
                ...performItemRowBaseSx,
                ...getPerformItemRowStyles(staged, played, isOnAir),
                ...getPerformSelectableRowHoverSx(canSelect, staged, played, isOnAir),
                flexDirection: 'column',
                alignItems: 'stretch',
                p: 0,
                overflow: 'hidden',
                cursor: canSelect ? 'pointer' : 'default',
              } as SxProps<Theme>
            }
          >
            <Box sx={{ bgcolor: '#000', width: '100%', aspectRatio: '16/9', minHeight: 0 }}>
              <LiveKitPreviewConnector
                livekitRoomId={livekitRoomId}
                mode="preview"
                publisherPlayerId={player.id}
                showCamera={category === 'cam'}
                showScreenshare={category === 'remote'}
                showControls={false}
                height="100%"
              />
            </Box>
            <Typography variant="body2" sx={{ px: 1, py: 0.75, flexShrink: 0 }}>
              {player.name ?? player.id}
            </Typography>
          </Paper>
        );
      })}
    </Box>
  );
}
